import { useApolloClient } from '@vue/apollo-composable'
import { computed, type ComputedRef, nextTick, onBeforeUnmount, type Ref, ref, watch } from 'vue'

import { useAiAgent } from '@/composables/useAiAgent'
import { ONBOARDING_AGENT_ID } from '@/lib/ai/agents/onboardingAgent'
import {
  hasMaklerOnboardingBootstrapInChat,
  hasScrapeCompletionInChat,
  type OnboardingSessionPayload,
  scrapeStatusFromSession,
} from '@/lib/ai/assistantApi'
import { toUiFieldPath } from '@/lib/ai/fieldPath'
import { maklerAssistantFieldKeys } from '@/lib/ai/maklerOnboardingFields'
import { getDataEntryFieldLabelKey } from '@/lib/ai/nextFieldPrompt'
import { awaitingFieldFromSession, genaisisPathToMaklerFormKey } from '@/lib/ai/onboardingChat'
import BOOTSTRAP_ASSISTANT from '@/lib/ai/queries/BootstrapAssistantOnboardingChat.gql'
import bus, { eventNames } from '@/lib/eventBus'
import COMPANY from '@/modules/company/queries/Company.gql'
import UPDATE_COMPANY from '@/modules/company/queries/UpdateCompany.gql'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import { getApolloClient } from '@/vue-apollo'

import { normalizeCommissionForApi } from '../lib/onboardingCommission'
import {
  handleOnboardingImageUploaded,
  uploadOnboardingImageFromChat,
} from '../lib/onboardingImageUpload'
import type { OnboardingFormData } from '../lib/onboardingPrefill'
import { useAiActionHandler } from './useAiActionHandler'
import { getFirstMissingVisibleField, useOnboardingAiContext } from './useOnboardingAiContext'
import { useOnboardingCrmSnapshot } from './useOnboardingCrmSnapshot'
import { useOnboardingFeatures } from './useOnboardingFeatures'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CompanyData = Record<string, any>
type Translate = (key: string, ...args: unknown[]) => string

export function useOnboardingAssistant(options: {
  company: ComputedRef<CompanyData | null>
  formData: OnboardingFormData
  wizardStep: Ref<number>
  wizardStepSlug: ComputedRef<string>
  activeEnterDataMode: ComputedRef<'chat' | 'form'>
  hasBillingStep: ComputedRef<boolean>
  isAssistedOnboardingEnabled: ComputedRef<boolean>
  isUsOnboarding: ComputedRef<boolean>
  billingPrefillContact: ComputedRef<Record<string, unknown>>
  countOfGuides: ComputedRef<number>
  t: Translate
  basicInfoRef: Ref<{ stopLoading?: () => void } | null>
  form: {
    userEditedFields: Set<string>
    populateFormFromCompany: (c: CompanyData) => void
    applyImageField: (field: 'companyLogo' | 'teamPicture' | 'portraitImage', url: string) => void
    applyGenaisisSessionToForm: (s: OnboardingSessionPayload | null | undefined) => unknown
    flushPendingGenaisisPrefill: () => void
    applyChatFieldUpdate: (u: { field: string; value: unknown }) => void
    syncFormAfterAiConfirmedAction: (a: import('@/stores/aiAssistant').PendingAction) => void
    getAmbassadorFormDraftForAi: () => Record<string, string> | null
    enterDataContextValues: (o?: Record<string, string>) => Record<string, unknown>
    genaisisScrapeCompleteNotified: Ref<boolean>
  }
  stepFromSlug: (slug: string) => number
  setWizardStep: (step: number) => void
  onContinueToBilling: () => void
  syncAiContext: Ref<() => void>
  refetchCompany: () => Promise<unknown>
}) {
  const {
    company,
    formData,
    wizardStep,
    wizardStepSlug,
    activeEnterDataMode,
    hasBillingStep,
    isAssistedOnboardingEnabled,
    isUsOnboarding,
    billingPrefillContact,
    countOfGuides,
    t,
    basicInfoRef,
    form,
    stepFromSlug,
    setWizardStep,
    onContinueToBilling,
    syncAiContext,
    refetchCompany,
  } = options

  const aiStore = useAiAssistantStore()
  const { isOnboardingWebScrapingEnabled } = useOnboardingFeatures()
  const { resolveClient } = useApolloClient()
  const postBasicTransitionLoading = ref(false)
  const preparingDataStage = ref<'saving' | 'assistant'>('saving')
  const onboardingScrapePollCancelled = ref(false)
  const onboardingScrapePollRunning = ref(false)
  const genesisBootstrapDone = ref(false)
  // The user ticked "I don't have a website" on the basic-info step. This is an
  // explicit choice, not just an empty URL — it tells genaisis to skip the
  // website prompt + scrape, and suppresses the scrape status bar.
  const hasNoWebsite = ref(false)

  const aiStepSlug = ref('basic-info')
  const aiStepIndex = ref(1)
  const aiStepData = ref<Record<string, unknown>>({})
  const aiCrmContext = useOnboardingCrmSnapshot()
  const { syncContext } = useOnboardingAiContext({
    activeStepSlug: aiStepSlug,
    activeStepIndex: aiStepIndex,
    currentStepData: aiStepData,
    crmContext: aiCrmContext,
    enterDataMode: activeEnterDataMode,
    isUsOnboarding,
    // Suppress every scrape affordance (status bar, scrape context) when the
    // user declared they have no website — not only when the feature is off.
    enableWebsiteScrape: computed(
      () => isOnboardingWebScrapingEnabled.value && !hasNoWebsite.value
    ),
  })
  watch(aiCrmContext, () => syncContext(), { deep: true })
  useAiAgent(ONBOARDING_AGENT_ID)

  function buildAndSyncAiContext(
    imageOverrides: Record<string, string> = {},
    contextOptions: { forceSlug?: string } = {}
  ) {
    const slug = contextOptions.forceSlug ?? wizardStepSlug.value
    aiStepSlug.value = slug
    aiStepIndex.value = wizardStep.value
    const enterData = form.enterDataContextValues(imageOverrides)
    const dataMap: Record<string, Record<string, unknown>> = {
      'basic-info': { name: formData.name, website: formData.website },
      'enter-data': enterData,
      payment: {
        firstname: billingPrefillContact.value.firstname,
        lastname: billingPrefillContact.value.lastname,
        phone: billingPrefillContact.value.phone,
        email: billingPrefillContact.value.email,
        paymentMethod: company.value?.billing?.paymentMethod ?? null,
      },
      guide: {
        selectedLandingpages: company.value?.bookedLandingpageSlugs || [],
        countOfGuides: countOfGuides.value,
      },
      review: { ...enterData },
    }
    aiStepData.value = dataMap[slug] ?? {}
    syncContext()
  }
  syncAiContext.value = () => buildAndSyncAiContext()

  useAiActionHandler({
    companyRef: company as unknown as Ref<CompanyData | null>,
    setStep: setWizardStep,
    getStep: stepFromSlug,
    onConfirmedActionSuccess: (action) => form.syncFormAfterAiConfirmedAction(action),
    getAmbassadorFormDraft: () => form.getAmbassadorFormDraftForAi(),
  })

  function isBootstrapFieldEmpty(val: unknown) {
    return val == null || String(val).trim() === ''
  }
  function bootstrapFieldValue(field: string) {
    const c = company.value
    if (field === 'companyLogo') {
      return formData.companyLogo || c?.images?.logo?.url || ''
    }
    if (field === 'teamPicture') {
      return formData.teamPicture || c?.images?.team?.url || ''
    }
    if (field === 'portraitImage') {
      return formData.portraitImage || c?.ambassador?.image?.url || ''
    }
    return (formData as Record<string, unknown>)[field]
  }
  function genesisBootstrapInput() {
    const contextOptions = postBasicTransitionLoading.value ? { forceSlug: 'enter-data' } : {}
    buildAndSyncAiContext({}, contextOptions)
    const websiteUrl = (formData.website || '').trim() || null
    const scrapeEnabled = isOnboardingWebScrapingEnabled.value
    const missing = maklerAssistantFieldKeys(isUsOnboarding.value).filter((field) =>
      isBootstrapFieldEmpty(bootstrapFieldValue(field))
    )
    return {
      websiteUrl: scrapeEnabled ? websiteUrl : null,
      fetchingWebsiteDetails: scrapeEnabled && Boolean(websiteUrl),
      afterWebsiteScrape: false,
      // Explicit signal that the user has no website, so genaisis skips the
      // "what is your website?" intake question and the scrape (see the
      // no-website contract doc in the genaisis repo).
      hasNoWebsite: hasNoWebsite.value,
      crmIsConnected: Boolean(aiCrmContext.value?.isActive),
      missingFields: missing.length ? missing : undefined,
      enterDataMode: activeEnterDataMode.value,
      billingStepRequired: hasBillingStep.value,
    }
  }

  function appendGenesisNextFieldNudge(text: string) {
    if ((formData.website || '').trim()) return text
    const first = getFirstMissingVisibleField('enter-data-assistant', formData, {
      isUsOnboarding: isUsOnboarding.value,
    })
    if (!first) return text
    const label = t(getDataEntryFieldLabelKey(first))
    return `${text}\n\n${t('ai-assistant.genesis.ask-next-field', { field: label })}`
  }

  function focusFirstMissingEnterDataField(session: OnboardingSessionPayload | null = null) {
    const fromSession = session ?? aiStore.lastOnboardingSession
    const awaitingKey = genaisisPathToMaklerFormKey(awaitingFieldFromSession(fromSession))
    if (awaitingKey) {
      aiStore.markFieldSuggestedByAi(toUiFieldPath(awaitingKey), { focus: true })
      return
    }
    const first = getFirstMissingVisibleField('enter-data-assistant', formData, {
      isUsOnboarding: isUsOnboarding.value,
    })
    if (first) aiStore.markFieldSuggestedByAi(toUiFieldPath(first), { focus: true })
  }

  async function bootstrapOnboardingAssistantChat(client: ReturnType<typeof getApolloClient>) {
    if (
      hasMaklerOnboardingBootstrapInChat(aiStore.messages) ||
      hasScrapeCompletionInChat(aiStore.messages)
    ) {
      return true
    }
    if (aiStore.messages.length > 0) aiStore.replaceMessages([])
    genesisBootstrapDone.value = false
    aiStore.onboardingBootstrapDone = false
    try {
      const { data } = await client.mutate({
        mutation: BOOTSTRAP_ASSISTANT,
        variables: { input: genesisBootstrapInput() },
      })
      const text = data?.bootstrapAssistantOnboardingChat
      if (text) {
        genesisBootstrapDone.value = true
        aiStore.onboardingBootstrapDone = true
        aiStore.replaceMessages([])
        aiStore.appendAssistantMessageImmediate(appendGenesisNextFieldNudge(text))
        if (!(formData.website || '').trim()) focusFirstMissingEnterDataField()
        return true
      }
    } catch (err) {
      console.warn('[Onboarding] bootstrapAssistantOnboardingChat failed', err)
    }
    aiStore.markAssistantUnavailable()
    return false
  }

  function stopBackgroundOnboardingScrapePoll() {
    onboardingScrapePollCancelled.value = true
  }

  async function startBackgroundOnboardingScrape(
    store: ReturnType<typeof useAiAssistantStore>,
    { skipWelcome = false } = {}
  ) {
    // Fall back to the persisted company website so a resumed scrape (e.g. after a
    // page reload restored to enter-data) works before `formData` is repopulated.
    const website = (formData.website || company.value?.links?.website || '').trim()
    if (!isOnboardingWebScrapingEnabled.value) return
    if (!website || onboardingScrapePollRunning.value) return
    onboardingScrapePollRunning.value = true
    onboardingScrapePollCancelled.value = false
    store.markScrapeProgressStarting()
    try {
      const applySession = (session: OnboardingSessionPayload | null | undefined) => {
        if (!session) return
        if (scrapeStatusFromSession(session) === 'completed') {
          store.setChatAfterScrapeFinished(session)
        }
        const completed = form.applyGenaisisSessionToForm(session)
        if (completed) focusFirstMissingEnterDataField(session)
      }
      const first = await store.syncOnboardingScrapeFromBackend({
        waitForScrape: false,
        website,
        skipWelcome,
      })
      applySession(first)
      if (first && scrapeStatusFromSession(first) === 'completed') return
      if (first && scrapeStatusFromSession(first) === 'failed') return
      for (let attempt = 0; attempt < 48; attempt++) {
        if (onboardingScrapePollCancelled.value || wizardStep.value !== 2) return
        await new Promise((resolve) => setTimeout(resolve, 2500))
        if (onboardingScrapePollCancelled.value || wizardStep.value !== 2) return
        const session = await store.syncOnboardingScrapeFromBackend({
          waitForScrape: false,
          website,
          skipWelcome,
        })
        applySession(session)
        const status = scrapeStatusFromSession(session)
        if (status === 'completed' || status === 'failed') return
      }
    } finally {
      onboardingScrapePollRunning.value = false
    }
  }

  async function initOnboardingAssistantChat() {
    const client = getApolloClient()
    const websiteUrl = (formData.website || company.value?.links?.website || '').trim()
    if (!isAssistedOnboardingEnabled.value) {
      aiStore.switchToManualOnboarding()
      if (isOnboardingWebScrapingEnabled.value) {
        if (websiteUrl) void startBackgroundOnboardingScrape(aiStore, { skipWelcome: true })
        else
          void aiStore.syncOnboardingScrapeFromBackend({ waitForScrape: false, skipWelcome: true })
      }
      return aiStore.lastOnboardingSession
    }
    if (
      hasMaklerOnboardingBootstrapInChat(aiStore.messages) ||
      hasScrapeCompletionInChat(aiStore.messages)
    ) {
      const cached = aiStore.lastOnboardingSession
      if (cached && scrapeStatusFromSession(cached) === 'completed') {
        aiStore.setChatAfterScrapeFinished(cached)
        form.applyGenaisisSessionToForm(cached)
        return cached
      }
      if (websiteUrl) void startBackgroundOnboardingScrape(aiStore)
      return cached
    }
    const restored = await aiStore.restoreEmbeddedOnboardingChat()
    if (restored) {
      const session = aiStore.lastOnboardingSession
      if (session) {
        form.applyGenaisisSessionToForm(session)
        if (scrapeStatusFromSession(session) === 'completed') {
          aiStore.setChatAfterScrapeFinished(session)
        } else if (websiteUrl) void startBackgroundOnboardingScrape(aiStore)
      }
      return session
    }
    await bootstrapOnboardingAssistantChat(client)
    if (websiteUrl) void startBackgroundOnboardingScrape(aiStore)
    else void aiStore.syncOnboardingScrapeFromBackend({ waitForScrape: false })
    return aiStore.lastOnboardingSession
  }

  async function handleBasicInfoContinue({
    websiteUrl,
    noWebsite = false,
  }: {
    websiteUrl: string
    noWebsite?: boolean
  }) {
    const companyName = (company.value?.name || formData.name || '').trim()
    hasNoWebsite.value = Boolean(noWebsite)
    formData.website = websiteUrl
    form.userEditedFields.add('website')
    if (companyName) formData.name = companyName
    postBasicTransitionLoading.value = true
    preparingDataStage.value = 'saving'
    form.genaisisScrapeCompleteNotified.value = false
    try {
      if (company.value) {
        try {
          await resolveClient().mutate({
            mutation: UPDATE_COMPANY,
            variables: {
              input: {
                companyId: company.value.id,
                ...(companyName ? { name: companyName } : {}),
                links: {
                  website: websiteUrl,
                  imprint: company.value.links?.imprint ?? null,
                  about: company.value.links?.about ?? null,
                  privacy: company.value.links?.privacy ?? null,
                },
                address: {
                  street: company.value.address?.street ?? null,
                  zip: company.value.address?.zip ?? null,
                  city: company.value.address?.city ?? null,
                  country: company.value.address?.country ?? null,
                },
                commission: normalizeCommissionForApi(formData.commission) || null,
              },
            },
            refetchQueries: [{ query: COMPANY, variables: { id: company.value.id } }],
          })
        } catch (err) {
          console.error('[Onboarding] Failed to save basic info:', err)
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'error',
            text: t('alerts.settings.company.error'),
          })
        }
      }
      await nextTick()
      if (company.value) form.populateFormFromCompany(company.value)
      aiStore.setOnboardingEmbeddedChatActive(true)
      preparingDataStage.value = isAssistedOnboardingEnabled.value ? 'assistant' : 'saving'
      buildAndSyncAiContext({}, { forceSlug: 'enter-data' })
      if (isAssistedOnboardingEnabled.value) {
        await bootstrapOnboardingAssistantChat(getApolloClient())
      } else {
        aiStore.switchToManualOnboarding()
      }
      setWizardStep(2)
      await nextTick()
      buildAndSyncAiContext()
    } finally {
      postBasicTransitionLoading.value = false
      preparingDataStage.value = 'saving'
    }
    basicInfoRef.value?.stopLoading?.()
    aiStore.open()
    if ((formData.website || '').trim()) {
      void startBackgroundOnboardingScrape(aiStore, {
        skipWelcome: !isAssistedOnboardingEnabled.value,
      })
    }
  }

  async function onImageUploaded(
    payload: { kind?: 'logo' | 'team' | 'profile'; url?: string } = {}
  ) {
    await handleOnboardingImageUploaded({
      ...payload,
      wizardStep: wizardStep.value,
      getCompany: () => company.value,
      refetchCompany,
      populateFormFromCompany: form.populateFormFromCompany,
      applyImageField: form.applyImageField,
      buildAndSyncAiContext,
      enterDataContextValues: form.enterDataContextValues,
    })
  }

  function uploadFromChat(payload: { formKey: string; file: File }) {
    return uploadOnboardingImageFromChat(
      {
        company: company.value,
        mutate: (opts) => resolveClient().mutate(opts),
        query: (opts) => resolveClient().query(opts),
        t,
        onImageUploaded,
      },
      payload
    )
  }

  const preparingDataStatusText = computed(() => {
    const key = `onboarding.wizard.preparing-data.stages.${preparingDataStage.value}`
    const translated = t(key)
    return translated !== key ? translated : t('onboarding.wizard.preparing-data.hint')
  })

  aiStore.setOnboardingSessionPollHandler((session) => {
    const completed = form.applyGenaisisSessionToForm(session)
    if (completed) focusFirstMissingEnterDataField(session)
  })
  aiStore.setOnboardingFieldUpdateHandler((update) => form.applyChatFieldUpdate(update))
  aiStore.setOnboardingImageUploadHandler((payload) => uploadFromChat(payload))
  bus.$on(eventNames.ONBOARDING_CONTINUE_TO_BILLING, onContinueToBilling)

  onBeforeUnmount(() => {
    stopBackgroundOnboardingScrapePoll()
    bus.$off(eventNames.ONBOARDING_CONTINUE_TO_BILLING, onContinueToBilling)
    aiStore.setOnboardingEmbeddedChatActive(false)
    aiStore.setOnboardingSessionPollHandler(null)
    aiStore.setOnboardingFieldUpdateHandler(null)
    aiStore.setOnboardingImageUploadHandler(null)
  })

  watch(postBasicTransitionLoading, (loading) => {
    if (loading || wizardStep.value !== 2) return
    nextTick(() => {
      const session = aiStore.lastOnboardingSession
      if (session && scrapeStatusFromSession(session) === 'completed') {
        aiStore.setChatAfterScrapeFinished(session)
        return
      }
      if (
        hasScrapeCompletionInChat(aiStore.messages) ||
        hasMaklerOnboardingBootstrapInChat(aiStore.messages)
      ) {
        return
      }
      void initOnboardingAssistantChat()
    })
  })

  return {
    postBasicTransitionLoading,
    preparingDataStatusText,
    buildAndSyncAiContext,
    initOnboardingAssistantChat,
    handleBasicInfoContinue,
    onImageUploaded,
    stopBackgroundOnboardingScrapePoll,
  }
}
